import BuyCurrencyIcon from 'assets/ver2/icons/header/buy-currency-icon.svg';
import SearchIcon from 'assets/ver2/icons/search-icon.svg';
import SortAZIcon from 'assets/ver2/icons/sort-az-icon.svg';
import SortZAIcon from 'assets/ver2/icons/sort-za-icon.svg';
import FXRatesSection from 'components/Dashboard/components/FXRatesSection';
import FXRatesSectionMobile from 'components/Dashboard/components/FXRatesSection/mobile';
import FXTopRatesSection from 'components/Dashboard/components/FXTopRatesSection';
import FXTopRatesSectionMobile from 'components/Dashboard/components/FXTopRatesSection/mobile';
import ButtonCommon from 'components/common/ButtonCommon';
import CollapsibleSideMenu from 'components/common/CollapsibleSideMenu';
import OuterBodyContent from 'components/common/CollapsibleSideMenu/OuterBodyContent';
import OuterPathContent, {
  DateTimeSection,
  SearchFilterSection,
  SubTitle,
  Title,
} from 'components/common/CollapsibleSideMenu/OuterPathContent';
import MobileTabMenu from 'components/common/MobileTabMenu';
import NavbarFooter from 'components/common/NavbarFooter/ver3';
import SizeBox from 'components/common/SizeBox';
import CalculatorModal from 'components/fx/CalculatorModal';
import {
  COLLAPSED_SIDEBAR_WIDTH,
  COLLAPSE_SIDEBAR_WIDTH,
  DESKTOP_MIN_WIDTH,
  DESKTOP_STANDARD_WIDTH,
  ORDER_EMAIL,
  SCREEN_ID_EXCHANGE_HOME,
} from 'constants';
import { datePicker, searchTextValid } from 'helpers';
import Cookies from 'helpers/cookies';
import { checkMaintenance } from 'helpers/maintenance';
import { FX_MAKE_PAYMENT_KEY } from 'hooks/fx/useFxAddTransaction';
import useFxFormAddTransactionStore from 'hooks/fx/useFxFormAddTransactionStore';
import useAutoShowEventModal from 'hooks/useAutoShowEventModal';
import { useDetectDevice } from 'hooks/useDetectDevice';
import useExchangeRates from 'hooks/useExchangeRates';
import useLang from 'hooks/useLang';
import useStore from 'hooks/useStore';
import { isEmpty } from 'lodash';
import React, { useEffect, useState } from 'react';
import { SET_SHOW_LOADING } from 'store/action';
import styled from 'styled-components';

const SCREEN_ID = SCREEN_ID_EXCHANGE_HOME;

const FxDashboardPage = ({ allFxRates }) => {
  const { t, locate } = useLang();
  const { state, dispatch } = useStore();
  const { isDesktop } = useDetectDevice();
  const { isMenuExpand } = state;

  const { fxCurrenciesPrefer } = useExchangeRates();

  const [topRates, setTopRates] = useState([]);
  const [remainingRates, setRemainingRates] = useState([]);
  const [filteredRates, setFilteredRates] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isAscending, setIsAscending] = useState(true);
  const [isShowCalculatorModal, setShowCalculatorModal] = useState(false);

  const { resetAddTransactionInformation } = useFxFormAddTransactionStore();

  const isSearchInvalid = !searchTextValid(searchQuery);
  const visibleRates = isSearchInvalid ? [] : filteredRates;

  const isExchangeRatesEmpty = !Boolean(topRates);
  const isRemainingRatesEmpty = !Boolean(remainingRates);

  const getTopRatesAndRemaining = (allFxRates, fxCurrenciesPrefer) => {
    if (!Array.isArray(allFxRates) || !Array.isArray(fxCurrenciesPrefer)) {
      throw new Error(
        'Both allFxRates and fxCurrenciesPrefer should be arrays.',
      );
    }

    const filteredArray = allFxRates.filter((item) =>
      fxCurrenciesPrefer.includes(item.currencyCode),
    );

    const insertIntoTop5 = (top5Array, item) => {
      if (top5Array.length < 5) {
        top5Array.push(item);
      } else {
        let minIndex = 0;
        for (let i = 1; i < 5; i++) {
          if (
            parseFloat(top5Array[i].rate) < parseFloat(top5Array[minIndex].rate)
          ) {
            minIndex = i;
          }
        }
        if (parseFloat(item.rate) > parseFloat(top5Array[minIndex].rate)) {
          top5Array[minIndex] = item;
        }
      }
    };

    const topRates = [];
    filteredArray.forEach((item) => insertIntoTop5(topRates, item));

    // Sort topRates to match the order of fxCurrenciesPrefer
    topRates.sort(
      (a, b) =>
        fxCurrenciesPrefer.indexOf(a.currencyCode) -
        fxCurrenciesPrefer.indexOf(b.currencyCode),
    );

    const remainingRates = allFxRates.filter(
      (item) =>
        !topRates.some((topItem) => topItem.currencyCode === item.currencyCode),
    );

    setFilteredRates(remainingRates);

    return { topRates, remainingRates };
  };
  useEffect(() => {
    if (allFxRates && fxCurrenciesPrefer) {
      try {
        const { topRates, remainingRates } = getTopRatesAndRemaining(
          allFxRates,
          fxCurrenciesPrefer,
        );
        setTopRates(topRates);
        setRemainingRates(remainingRates);
      } catch (error) {
        checkMaintenance(error);
        console.error(error.message);
        dispatch({ type: SET_SHOW_LOADING, payload: false });
      }
    }
  }, [allFxRates, fxCurrenciesPrefer]);

  const handleSort = () => {
    setIsAscending(!isAscending);
    const sortedArray = [...filteredRates].sort((a, b) => {
      if (isAscending) {
        return a.currency.localeCompare(b.currency);
      } else {
        return b.currency.localeCompare(a.currency);
      }
    });
    setFilteredRates(sortedArray);
  };

  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleOnShowCalculatorModal = () => {
    Cookies.remove(FX_MAKE_PAYMENT_KEY);
    Cookies.remove(ORDER_EMAIL);
    resetAddTransactionInformation();
    setShowCalculatorModal(true);
  };
  const handleOnCloseCalculatorModal = () => {
    setShowCalculatorModal(false);
  };

  useEffect(() => {
    if (!searchTextValid(searchQuery)) {
      setFilteredRates([]);
    } else if (searchQuery) {
      setFilteredRates(
        allFxRates.filter((item) => {
          const lowerCaseQuery = searchQuery.trim().toLowerCase();
          return (
            item.currency.toLowerCase().includes(lowerCaseQuery) ||
            item.currencyCode.toLowerCase().includes(lowerCaseQuery) ||
            item.countryCode.toLowerCase().includes(lowerCaseQuery)
          );
        }),
      );
    } else {
      setFilteredRates(remainingRates);
    }
  }, [searchQuery]);

  useAutoShowEventModal({
    screenId: SCREEN_ID,
    params: [],
    modalStyles: {},
    modalOptions: {},
  });

  const renderFXTopRatesSection = () => {
    if (!isDesktop) {
      return (
        <OuterPathContent
          style={{
            boxShadow: 'none',
            marginBottom: '-16px',
          }}
        >
          <MobileTabMenu />
          <Title>{t('fx_exchange_rates_title')}</Title>
          <SizeBox height={4} />
          <DateTimeSection>
            {datePicker.getFullTimeStringTranslate(locate)}
          </DateTimeSection>
          <FXTopRatesSectionMobile FXRatesData={topRates} />
        </OuterPathContent>
      );
    }
    if (isExchangeRatesEmpty) return null;
    return (
      <>
        <OuterPathContent>
          <Title>{t('fx_exchange_rates_title')}</Title>
          <SizeBox height={5} />
          <DateTimeSection>
            {datePicker.getFullTimeStringTranslate(locate)}
            <ButtonCommon
              value={t('fx_buy_currency_label')}
              color="var(--c-primary)"
              background="var(--bg-primary)"
              isFill={true}
              styles={{
                paddingInline: 0,
              }}
              buttonIconStyles={{
                float: 'right',
                height: '50px',
                paddingInline: '30px',
                gap: '4px',
              }}
              iconSrc={BuyCurrencyIcon}
              onClick={handleOnShowCalculatorModal}
            />
          </DateTimeSection>
          <FXTopRatesSection FXRatesData={topRates} />
        </OuterPathContent>
        <SizeBox height={20} />
      </>
    );
  };

  const renderSearchFilterSection = (options = {}) => {
    const { styled = `` } = options || {};
    return (
      <SearchFilterSection styled={styled}>
        <SearchCurrencyStyled>
          <SearchInput
            type="text"
            value={searchQuery || ''}
            placeholder={t('fx_currency_search')}
            onChange={handleSearch}
            onKeyDown={handleSearch}
          />
          <SearchIconStyled src={SearchIcon} width={24} height={24} alt="" />
          {isSearchInvalid && (
            <SearchErrorStyled role="alert">
              {t('form_msg_search_validation')}
            </SearchErrorStyled>
          )}
          <ButtonCommon
            color="var(--ds-c-blue)"
            background="transparent"
            isFill={false}
            styles={{
              paddingInline: 0,
            }}
            borderNameVariable="--border-filter"
            iconSrc={isAscending ? SortZAIcon : SortAZIcon}
            onClick={handleSort}
          />
        </SearchCurrencyStyled>
      </SearchFilterSection>
    );
  };
  const renderFXRatesSection = () => {
    if (!isDesktop) {
      return (
        <>
          <Title>{t('fx_other_currencies_title')}</Title>
          {renderSearchFilterSection()}
          <SizeBox height={16} />
          <FXRatesSectionMobile FXRatesData={visibleRates} />
        </>
      );
    }
    if (isRemainingRatesEmpty) return null;
    const getMaxWidthOfOuterPathContent = () => {
      const sidebarWidth = isMenuExpand
        ? COLLAPSE_SIDEBAR_WIDTH
        : COLLAPSED_SIDEBAR_WIDTH;
      const max =
        DESKTOP_STANDARD_WIDTH *
        ((DESKTOP_STANDARD_WIDTH - sidebarWidth) / DESKTOP_STANDARD_WIDTH);
      return max - 30;
    };
    return (
      <OuterPathContent
        style={{
          maxWidth: `${getMaxWidthOfOuterPathContent()}px`,
        }}
      >
        <SubTitle>{t('fx_other_currencies_title')}</SubTitle>
        {renderSearchFilterSection({
          styled: `margin-top: 16px;`,
        })}
        <SizeBox height={8} />
        <FXRatesSection FXRatesData={visibleRates} />
      </OuterPathContent>
    );
  };

  if (isEmpty(allFxRates) && isDesktop)
    return (
      <CollapsibleSideMenu>
        <OuterBodyContent></OuterBodyContent>
      </CollapsibleSideMenu>
    );
  if (isEmpty(allFxRates) && !isDesktop)
    return (
      <OuterBodyContent>
        <NavbarFooter />
      </OuterBodyContent>
    );
  if (!isDesktop) {
    return (
      <>
        {isShowCalculatorModal && (
          <CalculatorModal
            isOpen={isShowCalculatorModal}
            onClose={handleOnCloseCalculatorModal}
          />
        )}
        <OuterBodyContent>
          {renderFXTopRatesSection()}
          {renderFXRatesSection()}
          <NavbarFooter />
        </OuterBodyContent>
      </>
    );
  }
  return (
    <>
      {isShowCalculatorModal && (
        <CalculatorModal
          isOpen={isShowCalculatorModal}
          onClose={handleOnCloseCalculatorModal}
        />
      )}
      <CollapsibleSideMenu>
        <OuterBodyContent>
          {renderFXTopRatesSection()}
          {renderFXRatesSection()}
        </OuterBodyContent>
      </CollapsibleSideMenu>
    </>
  );
};

const SearchCurrencyStyled = styled.div`
  position: relative;
  height: 44px;
  width: calc(100vw - 32px);

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    height: unset;
    width: 30vw;
  }
`;
const SearchInput = styled.input`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 20px;

  color: var(--ds-c-grey-dark);
  border: 1px solid #b0b6bd;
  border-radius: 12px;
  background: transparent;
  width: calc(100% - 52px);
  height: 44px;
  padding: 10px;
  padding-right: 32px;
  float: left;
  margin-right: 8px;

  &:focus,
  &:focus-visible {
    border: 1px solid #b0b6bd !important;
    outline: none !important;
    box-shadow: none !important;
  }

  &::placeholder {
    font-family: var(--ff-primary);
    font-style: normal;
    font-weight: 500;
    font-size: 16px;
    line-height: 20px;

    color: var(--ds-c-blue-disabled) !important;

    @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
      font-size: 16px;
      line-height: 20px;
    }
  }

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    font-size: 16px;
    line-height: 20px;
    width: 287px;
  }
`;
const SearchIconStyled = styled.img`
  position: absolute;
  top: 10px;
  left: calc(100% - 84px);
  cursor: pointer;
  user-select: none;
  background: transparent;

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    left: 254px;
  }
`;
const SearchErrorStyled = styled.p`
  position: absolute;
  top: 48px;
  left: 0;
  width: calc(100% - 32px);
  margin: 0;
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 500;
  font-size: 12px;
  line-height: 14px;
  color: var(--ds-c-red);

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    width: 100%;
    font-size: 14px;
    line-height: 17px;
  }
`;

export default FxDashboardPage;
